/**
 * AFFILIATE TRANSACTION SERVICE
 *
 * Comprehensive transaction management for affiliate operations:
 * - Commission to shares conversion with atomic operations
 * - Share transfers between users with validation
 * - Commission withdrawal processing
 * - Audit logging for all financial transactions
 * - Rollback mechanisms for failed operations
 */

import { affiliateNotificationService } from './affiliateNotificationService';
import { validateCompanyTransfer, logCompanyTransferAudit } from '../companyTransferValidation';
import { getSessionHeaders } from '../serverSession';

// API helper for affiliate transaction operations - NO getServiceRoleClient() in browser
async function callAffiliateTransactionsAPI(operation: string, data: Record<string, any> = {}): Promise<any> {
 const response = await fetch('/api/user/affiliate-transactions', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ operation, data })
 });
 const result = await response.json();
 if (!response.ok) {
 throw new Error(result.error || 'API call failed');
 }
 return result;
}

// ============================================================================
// SHARE TRANSFERS DISABLED
// ============================================================================
// This feature has been permanently disabled due to multiple reports of
// shareholders attempting to sell shares in violation of platform terms.
// Share transfers will remain disabled until all 1,400,000 shares across
// the 19 phases have been sold.
// ============================================================================
const SHARE_TRANSFERS_ENABLED = false;

// ESCROW SECURITY FUNCTIONS - Prevent Double-Spending Vulnerabilities
// These functions implement atomic escrow operations to prevent race conditions
// between concurrent commission withdrawal and conversion requests

/**
 * Atomically check available balance and create escrow for commission request
 * @param {number} userId - User ID
 * @param {number} requestAmount - Amount to escrow
 * @param {string} requestType - 'withdrawal' or 'conversion'
 * @returns {Promise<{success: boolean, availableBalance?: number, error?: string}>}
 */
async function createCommissionEscrow(userId: number, requestAmount: number, requestType: string): Promise<{success: boolean, availableBalance?: number, error?: string}> {
 try {
 const result = await callAffiliateTransactionsAPI('createCommissionEscrow', {
 userId, requestAmount, requestType
 });

 if (!result.success) {
 console.error(`[ESCROW] Failed to create escrow for ${requestType}:`, result.error);
 return { success: false, error: result.error };
 }

 console.log(`[ESCROW] Created ${requestType} escrow: $${requestAmount} for user ${userId}`);
 return { success: true, availableBalance: result.availableBalance };
 } catch (error) {
 console.error(`[ESCROW] Exception creating escrow:`, error);
 return { success: false, error: 'Internal escrow error' };
 }
}

/**
 * Release escrow when request is rejected (only deduct from escrowed_amount)
 * @param {number} userId - User ID
 * @param {number} escrowAmount - Amount to release from escrow
 * @returns {Promise<{success: boolean, error?: string}>}
 */
async function releaseCommissionEscrow(userId: number, escrowAmount: number): Promise<{success: boolean, error?: string}> {
 try {
 const result = await callAffiliateTransactionsAPI('releaseCommissionEscrow', {
 userId, escrowAmount
 });

 if (!result.success) {
 console.error(`[ESCROW] Failed to release escrow:`, result.error);
 return { success: false, error: result.error };
 }

 console.log(`[ESCROW] Released escrow: $${escrowAmount} for user ${userId}`);
 return { success: true };
 } catch (error) {
 console.error(`[ESCROW] Exception releasing escrow:`, error);
 return { success: false, error: 'Internal escrow error' };
 }
}

/**
 * Get available commission balance (usdt_balance - escrowed_amount)
 * @param {number} userId - User ID
 * @returns {Promise<{availableBalance: number, totalBalance: number, escrowedAmount: number}>}
 */
async function getAvailableCommissionBalance(userId: number): Promise<{availableBalance: number, totalBalance: number, escrowedAmount: number}> {
 try {
 const result = await callAffiliateTransactionsAPI('getAvailableCommissionBalance', { userId });

 console.log(`[ESCROW] Balance check for user ${userId}: Total=$${result.totalBalance}, Escrowed=$${result.escrowedAmount}, Available=$${result.availableBalance}`);

 return {
 availableBalance: Math.max(0, result.availableBalance || 0),
 totalBalance: result.totalBalance || 0,
 escrowedAmount: result.escrowedAmount || 0
 };
 } catch (error) {
 console.error(`[ESCROW] Exception getting balance:`, error);
 return { availableBalance: 0, totalBalance: 0, escrowedAmount: 0 };
 }
}

// RATE LIMITING - Prevent abuse of transfer operations
const TRANSFER_RATE_LIMIT = {
 maxTransfers: 5, // Maximum transfers allowed
 windowHours: 1, // Time window in hours
};

/**
 * Check if user has exceeded transfer rate limit
 * @param {number} userId - User ID
 * @returns {Promise<{allowed: boolean, remainingTransfers: number, error?: string}>}
 */
async function checkTransferRateLimit(userId: number): Promise<{allowed: boolean, remainingTransfers: number, error?: string}> {
 try {
 const result = await callAffiliateTransactionsAPI('checkTransferRateLimit', { userId });

 if (!result.allowed) {
 console.warn(`[RATE_LIMIT] User ${userId} exceeded transfer rate limit`);
 }

 return { allowed: result.allowed, remainingTransfers: result.remainingTransfers };
 } catch (error) {
 console.error('Exception checking transfer rate limit:', error);
 return { allowed: false, remainingTransfers: 0, error: 'Rate limit check failed' };
 }
}

/**
 * Validate that user is not trying to be their own sponsor (except userID 4)
 * @param {number} userId - User ID
 * @param {string} sponsorUsername - Sponsor username to validate
 * @returns {Promise<{isValid: boolean, error?: string}>}
 */
async function validateSponsorAssignment(userId: number, sponsorUsername: string): Promise<{isValid: boolean, error?: string}> {
 try {
 const result = await callAffiliateTransactionsAPI('validateSponsorAssignment', {
 userId,
 sponsorUsername: sponsorUsername.toLowerCase().trim()
 });

 // Check if user is trying to be their own sponsor (except userID 4)
 if (result.isValid && result.sponsorId === userId && userId !== 4) {
 return { isValid: false, error: 'You cannot be your own sponsor' };
 }

 return { isValid: result.isValid, error: result.error };
 } catch (error) {
 console.error(`[SPONSOR_VALIDATION] Exception validating sponsor:`, error);
 return { isValid: false, error: 'Error validating sponsor' };
 }
}

export interface ConversionTransaction {
 userId: number;
 usdtAmount: number;
 sharesReceived: number;
 sharePrice: number;
 phaseId: number;
 packageId?: 'P1' | 'P2' | 'P3' | 'P4' | 'P5';
 packageQty?: number;
 packages?: { packageId: 'P1' | 'P2' | 'P3' | 'P4' | 'P5'; qty: number }[];
 transactionId?: string;
}

export interface ShareTransferTransaction {
 senderId: number;
 recipientId: number;
 sharesTransferred: number;
 transactionId?: string;
}

export interface WithdrawalTransaction {
 userId: number;
 amount: number;
 currency: 'USDT' | 'SHARES';
 walletAddress?: string;
 network?: string;
 transactionId?: string;
}

export interface TransactionResult {
 success: boolean;
 transactionId?: string;
 error?: string;
 data?: any;
}

class AffiliateTransactionService {
 // Track in-progress conversions to prevent duplicates
 private processingConversions: Set<string> = new Set();

 /**
 * Convert USDT commission to shares with atomic operations
 */
 async processCommissionToSharesConversion(transaction: ConversionTransaction): Promise<TransactionResult> {
 // DUPLICATE PREVENTION: Create unique key for this conversion attempt
 const packageKey = (transaction.packages && transaction.packages.length)
 ? transaction.packages.map((line) => `${line.packageId}x${line.qty}`).join('|')
 : `${transaction.packageId || 'plain'}x${transaction.packageQty || 0}`;
 const conversionKey = `${transaction.userId}-${packageKey}-${transaction.usdtAmount}-${transaction.sharesReceived}`;

 // Check if this exact conversion is already being processed
 if (this.processingConversions.has(conversionKey)) {
 console.warn(`[DUPLICATE] Conversion already in progress for user ${transaction.userId}, amount: $${transaction.usdtAmount}`);
 return {
 success: false,
 error: 'Conversion already in progress. Please wait for it to complete.'
 };
 }

 // Mark as processing
 this.processingConversions.add(conversionKey);

 try {
 console.log('Processing USDT to Shares conversion:', transaction);

 // CHECK FOR RECENT DUPLICATE: Prevent duplicate conversions within 30 seconds (via API)
 const duplicateCheck = await callAffiliateTransactionsAPI('checkRecentConversions', {
 userId: transaction.userId,
 usdtAmount: transaction.usdtAmount,
 sharesReceived: transaction.sharesReceived
 });

 if (duplicateCheck.hasDuplicates) {
 console.warn(`[DUPLICATE] Found ${duplicateCheck.count} recent conversion(s) for user ${transaction.userId} with same amount in last 30 seconds`);
 return {
 success: false,
 error: 'A similar conversion was just processed. Please wait a moment and check your balance.'
 };
 }

 // VALIDATION: Amount must be exact multiple of share price
 const calculatedShares = transaction.usdtAmount / transaction.sharePrice;
 if (!Number.isInteger(calculatedShares)) {
 const error = `Invalid conversion amount: $${transaction.usdtAmount} is not an exact multiple of share price $${transaction.sharePrice}. You can only convert exact multiples (e.g., $${transaction.sharePrice}, $${transaction.sharePrice * 2}, $${transaction.sharePrice * 5}, etc.)`;
 console.error('[VALIDATION]', error);
 throw new Error(error);
 }

 const sharesReceived = transaction.sharesReceived; // EXACT decimal value, NO Math.floor

	 // v4.8.18: Server-side raw-balance conversion.  Conversion availability is
	 // based on commission_balances.usdt_balance, not usdt_balance - escrowed_amount
	 // (the latter is the withdrawal ceiling and can include stale withdrawal escrow).
	 // The API reserves the commission and leaves the conversion pending.
	 // Shares are credited only after an admin verifies the reserved commission.
 // The client no longer touches commission_balances directly.
 let newUsdtBalance: number | undefined;
	 const commissionResult = await callAffiliateTransactionsAPI('processRawBalanceConversion', {
	 userId: transaction.userId,
	 usdtAmount: transaction.usdtAmount,
	 sharesReceived: transaction.sharesReceived,
	 sharePrice: transaction.sharePrice,
	 phaseId: transaction.phaseId,
	 packageId: transaction.packageId,
	 packageQty: transaction.packageQty,
	 packages: transaction.packages
 });

 if (!commissionResult.success) {
 console.error('Conversion processing failed:', commissionResult.error);
 throw new Error(commissionResult.error || 'Conversion processing failed');
 }

 newUsdtBalance = commissionResult.newBalance;
	 const conversionId = commissionResult.conversionId;
	 const pendingReview = commissionResult.pending === true || commissionResult.status === 'pending'
	 console.log(pendingReview ? 'Conversion reserved for admin review' : 'Conversion processed successfully via raw-balance server RPC');

 // Log audit trail for the reserved conversion
 await this.logAuditTrail({
 user_id: transaction.userId,
 action: pendingReview ? 'commission_conversion_pending' : 'commission_conversion_completed',
 details: {
 usdt_converted: transaction.usdtAmount,
 shares_received: sharesReceived,
 share_price: transaction.sharePrice,
 package_id: transaction.packageId || null,
 package_qty: transaction.packageQty || null,
	 conversion_id: conversionId,
 status: pendingReview ? 'pending_admin_review' : 'automatically_approved',
 new_usdt_balance: newUsdtBalance
 },
 amount: transaction.usdtAmount,
 currency: 'USDT'
 });

 if (!pendingReview) {
 // Send conversion notification email via server-side API
 try {
 console.log('Sending conversion notification email via API...');
 await callAffiliateTransactionsAPI('sendConversionEmail', {
 userId: transaction.userId,
 usdtAmount: transaction.usdtAmount,
 sharesReceived,
 sharePrice: transaction.sharePrice,
	 conversionId
 });
 console.log('Conversion notification email sent');
 } catch (emailError) {
 console.error('Conversion email error:', emailError);
 // Don't fail the conversion if email fails
 }
 }

 return {
 success: true,
 pending: pendingReview,
	 transactionId: conversionId,
 data: {
 newUsdtBalance,
 sharesReceived: transaction.sharesReceived,
 pending: pendingReview,
 packageId: transaction.packageId || null,
 packageQty: transaction.packageQty || null,
 buyerBonus: pendingReview ? 0 : (commissionResult.buyerBonus || 0),
 sponsorBonus: pendingReview ? 0 : (commissionResult.sponsorBonus || 0),
 giftsIssued: pendingReview ? false : commissionResult.giftsIssued === true,
 sponsorGiftIssued: pendingReview ? false : commissionResult.sponsorGiftIssued === true,
 giftAlert: commissionResult.giftAlert || null
 }
 };

 } catch (error) {
 console.error('Error processing USDT to Shares conversion:', error);

 // Log failed transaction
 await this.logAuditTrail({
 user_id: transaction.userId,
 action: 'commission_conversion_failed',
 details: {
 error: error instanceof Error ? error.message : 'Unknown error',
 usdt_amount: transaction.usdtAmount,
 shares_amount: transaction.sharesReceived
 },
 amount: transaction.usdtAmount,
 currency: 'USDT'
 });

 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 } finally {
 // Always remove from processing set when done
 const packageKey = (transaction.packages && transaction.packages.length)
 ? transaction.packages.map((line) => `${line.packageId}x${line.qty}`).join('|')
 : `${transaction.packageId || 'plain'}x${transaction.packageQty || 0}`;
 const conversionKey = `${transaction.userId}-${packageKey}-${transaction.usdtAmount}-${transaction.sharesReceived}`;
 this.processingConversions.delete(conversionKey);
 }
 }

 /**
 * Process share transfer between users with atomic operations
 * SECURITY FIX: Uses database RPC function for atomic transfer with proper locking
 *
 * ============================================================================
 * SHARE TRANSFERS DISABLED
 * ============================================================================
 * This function has been disabled due to multiple reports of users attempting
 * to sell shares in violation of platform terms.
 * ============================================================================
 */
 async processShareTransfer(transaction: ShareTransferTransaction): Promise<TransactionResult> {
 // ============================================================================
 // SHARE TRANSFERS DISABLED - Reject all transfer attempts
 // ============================================================================
 if (!SHARE_TRANSFERS_ENABLED) {
 console.warn('Share transfer rejected - transfers are disabled:', {
 senderId: transaction.senderId,
 recipientId: transaction.recipientId,
 shares: transaction.sharesTransferred
 });
 return {
 success: false,
 error: 'Share transfers have been permanently disabled. Due to multiple reports of shareholders attempting to sell shares in violation of our terms, this feature has been removed to protect the integrity of the shareholder ecosystem. Share transfers will remain disabled until all 1,400,000 shares across the 19 phases have been sold.'
 };
 }

 try {
 console.log('Processing share transfer:', transaction);

 // Basic client-side validation (server does full validation)
 if (transaction.senderId === transaction.recipientId && transaction.senderId !== 4) {
 throw new Error('You cannot transfer shares to yourself');
 }

 if (transaction.sharesTransferred <= 0) {
 throw new Error('Transfer amount must be positive');
 }

 // SECURITY FIX: Check rate limiting (max 5 transfers per hour)
 const rateLimitCheck = await checkTransferRateLimit(transaction.senderId);
 if (!rateLimitCheck.allowed) {
 throw new Error(`Rate limit exceeded. You can make up to ${TRANSFER_RATE_LIMIT.maxTransfers} transfers per hour. Please try again later.`);
 }
 console.log(`Rate limit check passed: ${rateLimitCheck.remainingTransfers} transfers remaining`);

 // KYC check removed - users can transfer shares without KYC verification

 // Use whole shares only for transfers (no transfer fee)
 const wholeSharesTransferred = Math.floor(transaction.sharesTransferred);

 // COMPANY ACCOUNT VALIDATION: Check if sender or recipient is a company account
 // Company accounts require approved KYC, active status, and cannot be frozen
 const companyValidation = await validateCompanyTransfer(
 transaction.senderId,
 transaction.recipientId,
 wholeSharesTransferred
 );

 if (!companyValidation.isValid) {
 // Log the blocked transfer attempt for audit
 await logCompanyTransferAudit(
 transaction.senderId,
 transaction.recipientId,
 wholeSharesTransferred,
 companyValidation,
 false
 );
 throw new Error(companyValidation.error || 'Company account validation failed');
 }

 // Log additional approval flag if needed
 if (companyValidation.requiresAdditionalApproval) {
 console.warn('LARGE COMPANY TRANSFER: Requires additional review', {
 sender: transaction.senderId,
 recipient: transaction.recipientId,
 shares: wholeSharesTransferred,
 senderType: companyValidation.senderAccountType,
 recipientType: companyValidation.recipientAccountType
 });
 }

 // SECURITY FIX: Use atomic RPC function with row-level locking (via API)
 const transferResult = await callAffiliateTransactionsAPI('processShareTransferAtomic', {
 senderId: transaction.senderId,
 recipientId: transaction.recipientId,
 sharesTransferred: wholeSharesTransferred,
 transferFee: 0, // No transfer fee
 totalDeducted: wholeSharesTransferred, // Same as shares transferred (no fee)
 transferReason: `Share transfer: ${wholeSharesTransferred} shares`
 });

 const rpcResult = transferResult.rpcResult;

 // Check RPC function result
 if (!rpcResult || !rpcResult.success) {
 throw new Error(rpcResult?.error || 'Transfer failed in database');
 }

 const transferId = rpcResult.transfer_id;
 console.log('Atomic transfer completed, ID:', transferId);

 // AUDIT: Extra logging for user 4 self-transfers
 if (transaction.senderId === 4 && transaction.recipientId === 4) {
 console.warn('ADMIN SELF-TRANSFER: User 4 transferred to themselves:', {
 shares: wholeSharesTransferred,
 transferId,
 timestamp: new Date().toISOString()
 });
 await this.logAuditTrail({
 user_id: 4,
 action: 'admin_self_transfer',
 details: {
 shares_transferred: wholeSharesTransferred,
 transfer_id: transferId,
 warning: 'Admin self-transfer - requires review'
 },
 amount: wholeSharesTransferred,
 currency: 'SHARES'
 });
 }

 // Trigger email notification for share transfer (non-blocking)
 try {
 const { emailNotificationTriggers } = await import('./emailNotificationTriggers');
 await emailNotificationTriggers.triggerShareTransferNotification(transferId);
 } catch (emailError) {
 console.warn('Failed to trigger share transfer email notification:', emailError);
 // Don't fail the transfer if email fails
 }

 // Log successful company transfer audit if applicable
 if (companyValidation.senderAccountType === 'company' || companyValidation.recipientAccountType === 'company') {
 await logCompanyTransferAudit(
 transaction.senderId,
 transaction.recipientId,
 wholeSharesTransferred,
 companyValidation,
 true
 );
 }

 console.log('Share transfer completed successfully');

 return {
 success: true,
 transactionId: transferId,
 data: {
 senderNewBalance: rpcResult.sender_balance_after,
 sharesTransferred: rpcResult.shares_transferred
 }
 };

 } catch (error) {
 console.error('Error processing share transfer:', error);

 // Log failed transaction
 await this.logAuditTrail({
 user_id: transaction.senderId,
 action: 'share_transfer_failed',
 details: {
 error: error instanceof Error ? error.message : 'Unknown error',
 recipient_id: transaction.recipientId,
 shares_amount: transaction.sharesTransferred
 },
 amount: transaction.sharesTransferred,
 currency: 'SHARES'
 });

 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Process withdrawal request with escrow security
 */
 async processWithdrawalRequest(
 userId: number,
 amount: number,
 walletAddress: string,
 network: string
 ): Promise<{ success: boolean; requestId?: number; error?: string }> {
 try {
 // Validate KYC status
 const kycValidation = await this.validateKYCForFinancialOperations(userId);
 if (!kycValidation.isValid) {
 return { success: false, error: kycValidation.error };
 }

 // Check minimum withdrawal amount
 if (amount < 10) {
 return { success: false, error: 'Minimum withdrawal amount is $10.00' };
 }

 // SECURE ESCROW: Check available balance and create escrow atomically
 console.log(`[ESCROW] Creating commission withdrawal escrow for user ${userId}, amount: $${amount}`);

 const escrowResult = await createCommissionEscrow(userId, amount, 'withdrawal');

 if (!escrowResult.success) {
 console.error(`[ESCROW] Failed to create withdrawal escrow:`, escrowResult.error);

 if (escrowResult.error?.includes('Insufficient available balance')) {
 // Get current balance info for detailed error message
 const balanceInfo = await getAvailableCommissionBalance(userId);
 return {
 success: false,
 error: `Insufficient available balance. Total: $${balanceInfo.totalBalance.toFixed(2)}, Escrowed: $${balanceInfo.escrowedAmount.toFixed(2)}, Available: $${balanceInfo.availableBalance.toFixed(2)}`
 };
 } else if (escrowResult.error?.includes('concurrent_request')) {
 return {
 success: false,
 error: 'Another commission request is currently being processed. Please wait and try again.'
 };
 } else {
 return { success: false, error: escrowResult.error || 'Failed to secure funds for withdrawal' };
 }
 }

 // Create withdrawal request (escrow already secured) (via API)
 const withdrawalResult = await callAffiliateTransactionsAPI('createWithdrawalRequest', {
 withdrawalData: {
 user_id: userId,
 amount,
 wallet_address: walletAddress,
 network,
 status: 'pending',
 withdrawal_type: 'usdt',
 created_at: new Date().toISOString()
 }
 });

 const withdrawal = withdrawalResult.withdrawal;
 if (!withdrawal) {
 console.error('Error creating withdrawal request');

 // ROLLBACK: Release escrow if withdrawal creation failed
 console.log(`[ESCROW] Rolling back escrow due to withdrawal creation failure`);
 await releaseCommissionEscrow(userId, amount);

 return { success: false, error: 'Failed to create withdrawal request' };
 }

 // Send notification email
 await affiliateNotificationService.sendWithdrawalRequestNotification(
 userId,
 amount,
 walletAddress,
 network
 );

 // Log audit trail
 await this.logAuditTrail({
 user_id: userId,
 action: 'withdrawal_request_created',
 details: {
 amount,
 wallet_address: walletAddress,
 network,
 withdrawal_id: withdrawal.id
 },
 amount,
 currency: 'USDT'
 });

 return { success: true, requestId: withdrawal.id };
 } catch (error) {
 console.error('Error processing withdrawal request:', error);
 return { success: false, error: 'Internal server error' };
 }
 }

 /**
 * Validate KYC status for financial operations (via API)
 */
 private async validateKYCForFinancialOperations(userId: number): Promise<{ isValid: boolean; error?: string }> {
 try {
 const result = await callAffiliateTransactionsAPI('validateKYCForFinancialOperations', { userId });
 return { isValid: result.isValid, error: result.error };
 } catch (error) {
 console.error('Error validating KYC status:', error);
 return { isValid: false, error: 'Unable to verify KYC status. Please try again.' };
 }
 }

 /**
 * Log audit trail for financial transactions (via API)
 */
 private async logAuditTrail(auditData: {
 user_id: number;
 action: string;
 details: any;
 amount: number;
 currency: string;
 }): Promise<void> {
 try {
 await callAffiliateTransactionsAPI('logAuditTrail', { auditData });
 } catch (error) {
 console.error('Error logging audit trail:', error);
 // Don't throw - audit logging failure shouldn't break the main transaction
 }
 }
}

export const affiliateTransactionService = new AffiliateTransactionService();

// Export escrow functions for use in other components
export {
 createCommissionEscrow,
 releaseCommissionEscrow,
 getAvailableCommissionBalance,
 validateSponsorAssignment
};
